import { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from "react";

// Compact snapshot of what the current page is showing. Sent verbatim to
// the agent on every chat turn so it can answer "what am I looking at"
// without having to ask.
//
// Discipline:
//  - Keep it small (~8 KB stringified, hard cap below)
//  - Push structured data, not DOM / HTML
//  - `visible_rows` capped to 20 most-relevant entries
//  - Pages call setViewContext({...}) on mount + whenever filters/window
//    change. Cleared on unmount via the return value below.
export type ViewContext = {
  route: string;            // e.g. "/dashboard"
  title: string;            // page H1 — "Overview", "Data Sources", etc.
  time_window_days?: number;
  filters?: Record<string, unknown>;
  headline_kpis?: Record<string, unknown>;
  visible_rows?: unknown[]; // capped at 20 by setViewContext
  // Free-form per-page extension. Use sparingly.
  extra?: Record<string, unknown>;
};

type Ctx = {
  context: ViewContext | null;
  setViewContext: (next: ViewContext | null) => void;
};

const ViewCtx = createContext<Ctx | null>(null);

const MAX_ROWS = 20;
const MAX_BYTES = 8 * 1024;

export function ViewContextProvider({ children }: { children: React.ReactNode }) {
  const [context, setContext] = useState<ViewContext | null>(null);

  const setViewContext = useCallback((next: ViewContext | null) => {
    if (!next) {
      setContext(null);
      return;
    }
    const trimmed: ViewContext = {
      ...next,
      visible_rows: next.visible_rows?.slice(0, MAX_ROWS),
    };
    setContext(trimmed);
  }, []);

  return (
    <ViewCtx.Provider value={{ context, setViewContext }}>
      {children}
    </ViewCtx.Provider>
  );
}

export function useViewContext() {
  const ctx = useContext(ViewCtx);
  if (!ctx) throw new Error("useViewContext used outside ViewContextProvider");
  return ctx;
}

// Convenience for pages: declare the snapshot once + on every dep change,
// and clear it on unmount so a stale context doesn't bleed to the next page.
export function usePublishViewContext(snapshot: ViewContext | null, deps: unknown[]) {
  const { setViewContext } = useViewContext();
  // Keep latest setter without re-running on identity changes.
  const setRef = useRef(setViewContext);
  setRef.current = setViewContext;
  useEffect(() => {
    setRef.current(snapshot);
    return () => setRef.current(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reason: deps array is supplied by the caller, which knows what state changes should trigger a re-publish (e.g. [days, data] on Dashboard). Forcing exhaustive-deps would either force callers to memoize every literal snapshot or re-publish on every render.
  }, deps);
}

// Slim the context to fit inside MAX_BYTES so a runaway page can't blow
// the agent's prompt budget. Returns a dict (not a string) — the agent
// now accepts structured view_context and formats it itself.
export function prepareViewContext(ctx: ViewContext | null): ViewContext | null {
  if (!ctx) return null;
  const json = JSON.stringify(ctx);
  if (json.length <= MAX_BYTES) return ctx;
  // Drop visible_rows first — they're usually the bulky part. If still
  // too big, return the headline_kpis-only stub so the agent still gets
  // something useful.
  const slim: ViewContext = { ...ctx, visible_rows: undefined, extra: { ...ctx.extra, truncated: true } };
  if (JSON.stringify(slim).length <= MAX_BYTES) return slim;
  return {
    route: ctx.route,
    title: ctx.title,
    time_window_days: ctx.time_window_days,
    headline_kpis: ctx.headline_kpis,
    extra: { truncated: true },
  };
}

// Stable hash for change detection so we only re-send view_context when
// it actually differs from the last value posted on this conversation.
// JSON-based — keys are ordered deterministically by the values passed
// in (pages build the dict the same way each time so this is fine).
export function viewContextSignature(ctx: ViewContext | null): string {
  return ctx ? JSON.stringify(ctx) : "";
}

// Memoized snapshot helper so callers don't re-publish on every render.
export function useMemoViewContext(build: () => ViewContext | null, deps: unknown[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reason: deps array is forwarded verbatim from the caller; same rationale as usePublishViewContext above.
  return useMemo(build, deps);
}
